// ============================================
// CHÁMOMILE - Private Event Inquiry
// ============================================
// Bulk online orders are self-serve (existing cart + bulk discount) — that flow
// stays as-is. Private events are negotiated (headcount, venue, custom
// pricing), so this deliberately does NOT try to extend checkout to cover them.
//
// Flow: capture just enough to quote from → save as a lead → hand off to
// WhatsApp with the summary pre-filled, so staff open a ready brief instead of
// a blank "hi do you do events?" chat.

import React, { useState, useEffect } from 'react';
import { Icons } from '../constants/icons';
import { EVENT_TYPES, submitEventInquiry, buildEventInquiryWhatsAppUrl, fetchEventGallery } from '../services/eventInquiryService';

const colors = {
  gold300: '#dccaa6',
  gold400: '#c9a962',
  gold500: '#b8943d',
  gold600: '#a07d2e',
  gold50: '#faf8f3',
  stone50: '#fafaf9',
  stone100: '#f5f5f4',
  stone200: '#e7e5e4',
  stone300: '#d6d3d1',
  stone400: '#a8a29e',
  stone500: '#78716c',
  stone600: '#57534e',
  stone800: '#292524',
  success: '#22c55e',
  error: '#ef4444',
};

const fieldLabel = { fontSize: '12px', fontWeight: 600, color: colors.stone600, display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' };
const fieldInput = { width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: '10px', border: `1px solid ${colors.stone200}`, fontSize: '14px', fontFamily: 'inherit', background: '#fff' };

// ============================================
// HOME-SCREEN ENTRY POINT
// ============================================
export const PrivateEventBanner = ({ onOpen }) => (
  <button
    onClick={onOpen}
    style={{
      width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: '12px',
      padding: '14px 16px', marginBottom: '16px',
      borderRadius: '16px',
      background: `linear-gradient(135deg, ${colors.gold400}, ${colors.gold600})`,
      boxShadow: '0 4px 14px rgba(184,148,61,0.28)',
      fontFamily: 'inherit',
    }}
  >
    <div style={{
      width: '38px', height: '38px', borderRadius: '11px', flexShrink: 0,
      background: 'rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icons.Sparkles size={19} style={{ color: '#fff' }} />
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Planning an event? Let's talk 🎉</div>
      <div style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.85)', marginTop: '2px' }}>
        Private parties, corporate orders, weddings — get a custom quote
      </div>
    </div>
    <Icons.ChevronRight size={16} style={{ color: '#fff', flexShrink: 0 }} />
  </button>
);

// ============================================
// INQUIRY MODAL
// ============================================
export const EventInquiryModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({ eventDate: '', notSureDate: false, headcount: '', eventType: '', contactName: '', contactPhone: '', notes: '', wantsFridgeRental: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [gallery, setGallery] = useState([]);

  // Past-event photos are social proof — load them when the form opens.
  useEffect(() => {
    if (!isOpen) return;
    fetchEventGallery().then(setGallery).catch(() => setGallery([]));
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const resetAndClose = () => {
    setForm({ eventDate: '', notSureDate: false, headcount: '', eventType: '', contactName: '', contactPhone: '', notes: '', wantsFridgeRental: false });
    setError(''); setSent(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.contactName.trim()) return setError('Please enter your name.');
    if (!form.contactPhone.trim()) return setError('Please enter a phone number so we can reach you.');

    setSubmitting(true);
    const input = {
      eventDate: form.notSureDate ? '' : form.eventDate,
      headcount: form.headcount,
      eventType: form.eventType,
      contactName: form.contactName,
      contactPhone: form.contactPhone,
      notes: form.notes,
      wantsFridgeRental: form.wantsFridgeRental,
    };

    const result = await submitEventInquiry(input);
    setSubmitting(false);

    // Open WhatsApp regardless of whether the Firestore save succeeded — a
    // backend hiccup shouldn't stop the customer from actually reaching us.
    // The lead is only lost if BOTH the save and the WhatsApp send fail, and
    // the customer sees a clear message for that case.
    const whatsappUrl = buildEventInquiryWhatsAppUrl(input);
    window.open(whatsappUrl, '_blank');

    if (result.success) {
      setSent(true);
    } else {
      setError('We opened WhatsApp for you, but could not save your inquiry on our side — please make sure to hit send in WhatsApp.');
      setSent(true);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
      onClick={resetAndClose}
    >
      <div
        style={{ background: '#fff', borderRadius: '24px 24px 0 0', maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '24px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '4px' }}>
          <div>
            <h2 style={{ fontSize: '19px', fontWeight: 800, color: colors.stone800, margin: 0 }}>Private Event Inquiry</h2>
            <p style={{ fontSize: '12.5px', color: colors.stone500, margin: '4px 0 0' }}>
              Tell us a little about it — we'll follow up on WhatsApp with a quote.
            </p>
          </div>
          <button onClick={resetAndClose} style={{ background: colors.stone100, border: 'none', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <Icons.X size={14} style={{ color: colors.stone600 }} />
          </button>
        </div>

        {sent ? (
          <div style={{ textAlign: 'center', padding: '28px 8px' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: `${colors.success}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <Icons.Check size={24} style={{ color: colors.success }} />
            </div>
            <h3 style={{ fontSize: '15px', fontWeight: 700, color: colors.stone800, margin: '0 0 6px' }}>Inquiry sent!</h3>
            <p style={{ fontSize: '13px', color: colors.stone500, margin: 0 }}>
              {error || "We've opened WhatsApp with your details — hit send there and we'll be in touch shortly. 🍵"}
            </p>
            <button onClick={resetAndClose} style={{ marginTop: '20px', padding: '11px 24px', borderRadius: '999px', border: 'none', background: colors.gold500, color: '#fff', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {/* Past events — proof we've done this before. Managed in POS
                Settings, so it stays empty rather than broken if none are set. */}
            {gallery.length > 0 && (
              <div>
                <label style={fieldLabel}><Icons.Camera size={13} /> Some of our past events</label>
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch' }}>
                  {gallery.map((url, i) => (
                    <img
                      key={`${url}-${i}`}
                      src={url}
                      alt={`Past Chámomile event ${i + 1}`}
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => { e.target.style.display = 'none'; }}
                      style={{
                        width: '120px', height: '120px', objectFit: 'cover',
                        borderRadius: '12px', flexShrink: 0, border: `1px solid ${colors.stone200}`,
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            <div>
              <label style={fieldLabel}><Icons.Sparkles size={13} /> Event Type</label>
              <select value={form.eventType} onChange={(e) => set('eventType', e.target.value)} style={fieldInput}>
                <option value="">Select type (optional)</option>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div>
              <label style={fieldLabel}><Icons.Calendar size={13} /> Event Date</label>
              <input
                type="date"
                value={form.eventDate}
                disabled={form.notSureDate}
                onChange={(e) => set('eventDate', e.target.value)}
                style={{ ...fieldInput, opacity: form.notSureDate ? 0.5 : 1 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '12px', color: colors.stone500, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.notSureDate} onChange={(e) => set('notSureDate', e.target.checked)} />
                Not sure yet
              </label>
            </div>

            <div>
              <label style={fieldLabel}><Icons.User size={13} /> Estimated Headcount / Quantity</label>
              <input type="text" value={form.headcount} onChange={(e) => set('headcount', e.target.value)} placeholder="e.g. 50 people, or 100 bottles" style={fieldInput} />
            </div>

            {/* Chiller rental — an add-on, so it's a yes/no interest flag here.
                The actual cost is quoted along with everything else on WhatsApp. */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer',
              padding: '12px', borderRadius: '12px',
              background: form.wantsFridgeRental ? colors.gold50 : colors.stone50,
              border: `1px solid ${form.wantsFridgeRental ? colors.gold300 : colors.stone200}`,
            }}>
              <input
                type="checkbox"
                checked={form.wantsFridgeRental}
                onChange={(e) => set('wantsFridgeRental', e.target.checked)}
                style={{ width: '17px', height: '17px', accentColor: colors.gold500, marginTop: '1px', flexShrink: 0 }}
              />
              <span>
                <span style={{ fontSize: '13px', fontWeight: 600, color: colors.stone800, display: 'block' }}>
                  Rent our HCK chiller for the event
                </span>
                <span style={{ fontSize: '11.5px', color: colors.stone500 }}>
                  Keeps the drinks cold and looks great on the table. Small rental fee — we'll
                  include it in your quote.
                </span>
              </span>
            </label>

            <div style={{ height: '1px', background: colors.stone100, margin: '2px 0' }} />

            <div>
              <label style={fieldLabel}><Icons.User size={13} /> Your Name *</label>
              <input type="text" value={form.contactName} onChange={(e) => set('contactName', e.target.value)} placeholder="e.g. Ahmad" style={fieldInput} required />
            </div>

            <div>
              <label style={fieldLabel}><Icons.Phone size={13} /> Phone Number *</label>
              <input type="tel" value={form.contactPhone} onChange={(e) => set('contactPhone', e.target.value)} placeholder="e.g. 012-345 6789" style={fieldInput} required />
            </div>

            <div>
              <label style={fieldLabel}><Icons.MessageCircle size={13} /> Notes (optional)</label>
              <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Venue, flavour preferences, anything else…" rows={3} style={{ ...fieldInput, resize: 'vertical' }} />
            </div>

            {/* Members are created from these details when an event is confirmed,
                so say so plainly here rather than surprising them later. */}
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              padding: '11px 12px', borderRadius: '10px',
              background: colors.gold50, border: `1px solid ${colors.gold300}`,
            }}>
              <Icons.Sparkles size={14} style={{ color: colors.gold600, flexShrink: 0, marginTop: '1px' }} />
              <span style={{ fontSize: '11.5px', color: colors.stone600, lineHeight: 1.5 }}>
                When your event order is confirmed, we'll set you up as a{' '}
                <strong style={{ color: colors.gold600 }}>Chámomile member</strong> using these
                details — so you get early access to new flavours and member-only perks. No signup
                needed.
              </span>
            </div>

            {error && !sent && (
              <div style={{ padding: '10px 12px', borderRadius: '10px', background: '#fee2e2', border: '1px solid #fecaca', color: colors.error, fontSize: '12.5px' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: '4px', padding: '14px', borderRadius: '999px', border: 'none',
                background: `linear-gradient(135deg, ${colors.gold400}, ${colors.gold600})`,
                color: '#fff', fontSize: '14px', fontWeight: 700, cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
            >
              <Icons.MessageCircle size={15} />
              {submitting ? 'Sending…' : 'Send Inquiry via WhatsApp'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default { PrivateEventBanner, EventInquiryModal };
